import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Lock, ArrowRight, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import DoctorShell from "@/components/DoctorShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

export default function LockedFeature() {
    const [sp] = useSearchParams();
    const featureCode = sp.get("code") || "";
    const navigate = useNavigate();
    const [entitlements, setEntitlements] = useState(null);
    const [doctor, setDoctor] = useState(null);

    useEffect(() => {
        Promise.all([
            api.get("/me/entitlements"),
            api.get("/me/doctor").catch(() => ({ data: null })),
        ]).then(([e, d]) => {
            setEntitlements(e.data);
            setDoctor(d.data);
        });
    }, []);

    if (!entitlements) return <DoctorShell><div className="text-[#17212B]/60">Loading…</div></DoctorShell>;
    const item = entitlements.items.find((i) => i.feature_code === featureCode);

    let title = "Feature locked";
    let description = "This feature is not available on your current plan.";
    let upgradeTo = null;

    if (item?.lock_reason === "temporarily_unavailable") {
        title = "Temporarily unavailable";
        description = `${item.name} is temporarily disabled by the platform team. It'll be back soon.`;
    } else if (item?.lock_reason === "coming_soon") {
        title = "Coming soon";
        description = `${item.name} is under active development. We'll email you when it's live.`;
    } else if (item?.lock_reason === "payment_pending") {
        title = "Complete your payment";
        description = "This feature unlocks once your advance payment is confirmed.";
    } else if (item?.lock_reason === "upgrade_needed" && item?.upgrade_to) {
        title = `Upgrade to ${item.upgrade_to}`;
        description = `${item.name} is included in the ${item.upgrade_to} package and above.`;
        upgradeTo = item.upgrade_to;
    }

    return (
        <DoctorShell doctor={doctor}>
            <div className="max-w-2xl mx-auto py-12" data-testid="locked-feature-page">
                <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4" data-testid="locked-back">
                    <ArrowLeft size={16} className="mr-2" /> Back
                </Button>
                <Card className="border-[#123B5D]/10">
                    <CardContent className="p-10 text-center">
                        <div className="w-16 h-16 rounded-full bg-[#123B5D]/10 mx-auto mb-4 flex items-center justify-center">
                            <Lock size={32} className="text-[#123B5D]" />
                        </div>
                        <div className="text-xs uppercase tracking-widest text-[#0F766E] mb-2" data-testid="locked-feature-code">{featureCode}</div>
                        <h2 className="text-2xl font-bold text-[#123B5D]" data-testid="locked-feature-title">{title}</h2>
                        <p className="text-[#17212B]/70 mt-3">{description}</p>
                        {upgradeTo && (
                            <Button
                                onClick={() => navigate("/doctor/packages")}
                                className="mt-6 bg-[#123B5D] hover:bg-[#0b2740] text-white"
                                data-testid="locked-upgrade-cta"
                            >
                                See {upgradeTo} package <ArrowRight size={16} className="ml-2" />
                            </Button>
                        )}
                        {item?.lock_reason === "payment_pending" && (
                            <Button
                                onClick={() => navigate("/doctor/packages")}
                                className="mt-6 bg-[#123B5D] hover:bg-[#0b2740] text-white"
                                data-testid="locked-pay-cta"
                            >
                                Complete payment <ArrowRight size={16} className="ml-2" />
                            </Button>
                        )}
                    </CardContent>
                </Card>
            </div>
        </DoctorShell>
    );
}
